// Live 7TV for the hosted overlay, over 7TV's EventAPI.
//
// Two things ride one socket for the whole page:
//   - each Twitch channel's 7TV emote set: an emote added, removed or renamed on
//     7TV works on stream at once, applied in place with no refetch;
//   - the channel's cosmetic entitlements, which deliver every present
//     chatter's PERSONAL emote set, so a 7TV subscriber's own emotes render
//     here the way they do in the app and the 7TV extension, and tell the
//     overlay the moment a chatter's paint or badge changes;
//   - the channel owner's 7TV account, because switching the channel to a
//     different set (a seasonal set, say) is an update of the owner, not of
//     either set. The new set is loaded whole and replaces the old in place.
//
// Protocol (github.com/SevenTV/EventAPI): HELLO carries the session id and the
// heartbeat interval; heartbeats flow server to client only and three missed
// ones mean the socket is dead; RESUME after a drop replays missed dispatches;
// close code 4007 is maintenance (wait at least 5 minutes), 4005 is a rate limit.

import {
  addSeventv,
  applySeventvChange,
  emitEmotesChanged,
  emoteScope,
  fetchProvider,
  isPersonalUseEmote,
  retryDelayMs,
  saveSeventvChannel,
  seventvActiveEmote,
  seventvChannelOf,
  seventvUserUrl,
  type EmoteLayer,
  type EmoteMap,
  type SevenTvPlatform,
} from './emoteMap';
import { seventvChangesFromDispatch, type SeventvEmoteChange } from './overlayConfig';

/** Hears a channel's 7TV emote set changes (adds, removes, renames). */
export type EmoteSetListener = (changes: SeventvEmoteChange[]) => void;

const EVENTAPI_URL = 'wss://events.7tv.io/v3';

const OP_DISPATCH = 0;
const OP_HELLO = 1;
const OP_RECONNECT = 4;
const OP_ACK = 5;
const OP_END_OF_STREAM = 7;
const OP_RESUME = 34;
const OP_SUBSCRIBE = 35;
const OP_UNSUBSCRIBE = 36;

const ENTITLEMENT_TYPES = ['entitlement.create', 'entitlement.update', 'entitlement.delete'];
const DEFAULT_HEARTBEAT_MS = 48_000;
const HEARTBEAT_MISSES = 3;
const RESUME_WAIT_MS = 5_000;
const BACKOFF_MIN_MS = 1_000;
const BACKOFF_MAX_MS = 60_000;
const MAINTENANCE_DELAY_MS = 300_000;
const RATE_LIMITED_DELAY_MS = 60_000;
/** An entitlement usually arrives beside a snapshot of the set it grants. Give
 *  the snapshot this long before fetching the set over REST instead. */
const SNAPSHOT_GRACE_MS = 2_000;
const FETCH_CONCURRENCY = 4;
/** Personal sets kept at once; the oldest goes first. A busy channel delivers
 *  one per present 7TV subscriber. */
const MAX_PERSONAL_SETS = 3_000;

interface ChannelWatch {
  /** The channel's key: a Twitch room id, or `kick:<id>` / `google:<id>`. */
  roomId: string;
  platform: SevenTvPlatform;
  /** The channel's id on its platform. */
  platformId: string;
  map: EmoteMap;
  refs: number;
  listeners: Set<EmoteSetListener>;
  /** Bumped by every set switch and re-read, so an older one still loading
   *  never lands over a newer one. */
  gen: number;
  /** A re-read or set load waiting to try again (7TV did not answer). */
  retry: ReturnType<typeof setTimeout> | null;
  /** The set being loaded (a switch, or a re-read of the set shown). It is
   *  subscribed before the load starts, and its edits are kept here in order,
   *  then laid over the loaded set, so an edit made mid-load is never lost. */
  pending: { setId: string; buffer: unknown[] } | null;
}

/** What one room is subscribed to on the socket's session. A RESUME restores
 *  exactly this, so it survives a drop. `sets` is the set shown, plus the one
 *  being loaded during a switch. */
interface RoomSubscription {
  sets: Set<string>;
  userId: string | null;
  /** Twitch channels also carry their chatters' entitlements (personal emotes,
   *  paint and badge changes), which are keyed by Twitch id. */
  entitlements: boolean;
}

/** Edits kept for one set load at most; past this the oldest go (they are the
 *  ones the load's answer is surest to already carry). */
const MAX_BUFFERED_EDITS = 1_000;

const channels = new Map<string, ChannelWatch>();
const subscribed = new Map<string, RoomSubscription>();
/** Sets a channel switched away from: an edit to one still in flight is not a
 *  chatter's personal set. */
const retiredSets = new Set<string>();
const personalSets = new Map<string, EmoteLayer>();
const userSets = new Map<string, Set<string>>();
const pendingFetch = new Set<string>();
const fetchQueue: string[] = [];
let activeFetches = 0;

let ws: WebSocket | null = null;
let sessionId: string | null = null;
let resumeFrom: string | null = null;
let awaitingResume = false;
let heartbeatMs = DEFAULT_HEARTBEAT_MS;
let deadTimer: ReturnType<typeof setTimeout> | null = null;
let retryTimer: ReturnType<typeof setTimeout> | null = null;
let resumeTimer: ReturnType<typeof setTimeout> | null = null;
let backoff = BACKOFF_MIN_MS;
let delayFloor = 0;

/**
 * Keep a channel's 7TV set live and, on Twitch, listen for its chatters'
 * personal emotes. `map.seventv` is patched in place, so the caller keeps using
 * the same map. `onChange` hears each change to the channel's set. Kick and
 * YouTube channels (`platform`) follow their set the same way; their chatters'
 * entitlements are not followed, because personal sets are keyed by Twitch id.
 * Returns a disposer.
 */
export function watchSevenTvChannel(
  channelId: string,
  map: EmoteMap,
  onChange?: EmoteSetListener,
  platform: SevenTvPlatform = 'TWITCH',
): () => void {
  const roomId = emoteScope(platform, channelId);
  const existing = channels.get(roomId);
  if (existing) {
    existing.refs += 1;
    existing.map = map;
    if (onChange) existing.listeners.add(onChange);
  } else {
    const w: ChannelWatch = {
      roomId,
      platform,
      platformId: channelId,
      map,
      refs: 1,
      listeners: new Set(onChange ? [onChange] : []),
      gen: 0,
      retry: null,
      pending: null,
    };
    channels.set(roomId, w);
    if (ws?.readyState === WebSocket.OPEN && sessionId && !awaitingResume) subscribeRoom(w);
    ensureConnected();
    // 7TV did not answer at load: the saved list stands in until it does.
    if (map.seventvStale) scheduleRetry(w, 0, () => void resyncChannel(w));
  }
  return () => {
    const w = channels.get(roomId);
    if (!w) return;
    if (onChange) w.listeners.delete(onChange);
    w.refs -= 1;
    if (w.refs > 0) return;
    if (w.retry) clearTimeout(w.retry);
    w.gen += 1;
    channels.delete(roomId);
    if (ws?.readyState === WebSocket.OPEN && !awaitingResume) unsubscribeRoom(roomId);
    if (channels.size === 0) shutdown();
  };
}

/** Hears a Twitch user whose 7TV paint or badge changed, by Twitch user id. */
export type SevenTvCosmeticListener = (twitchUserId: string) => void;

const cosmeticListeners = new Set<SevenTvCosmeticListener>();

/**
 * Hear 7TV paint and badge changes for chatters in the watched channels. They
 * ride the channel entitlement subscription the personal emotes already use,
 * so this opens nothing new. Returns a disposer.
 */
export function onSevenTvCosmeticChange(listener: SevenTvCosmeticListener): () => void {
  cosmeticListeners.add(listener);
  return () => {
    cosmeticListeners.delete(listener);
  };
}

/** A personal set as the identity answer carries it: [name, 7TV id, zero-width]. */
export interface PersonalSetAnswer {
  set_id: string;
  emotes: [string, string, 0 | 1][];
}

/**
 * A chatter's personal set as their identity answer carried it (7TV
 * subscribers only), installed while their first rows are still held, so they
 * show with their personal emotes instead of gaining them a moment later. The
 * entitlement feed stays the authority: it adds and removes sets later.
 */
export function installPersonalSet(twitchUserId: string, set: PersonalSetAnswer): void {
  if (!twitchUserId || !set?.set_id || !Array.isArray(set.emotes)) return;
  identityInstalled.set(twitchUserId, set.set_id);
  const layer: EmoteLayer = new Map();
  for (const row of set.emotes) {
    if (!Array.isArray(row) || typeof row[0] !== 'string' || typeof row[1] !== 'string') continue;
    layer.set(row[0], { id: row[1], url: `https://cdn.7tv.app/emote/${row[1]}/3x.webp`, zeroWidth: row[2] === 1, personal: true });
  }
  const sets = userSets.get(twitchUserId) ?? new Set<string>();
  sets.add(set.set_id);
  userSets.set(twitchUserId, sets);
  rememberSet(set.set_id, layer, false);
  emitEmotesChanged({ kind: 'personal', userIds: [twitchUserId], immediate: true });
}

/** Sets installed from identity answers, by Twitch id (installPersonalSet). */
const identityInstalled = new Map<string, string>();

/** A fresh identity answer carried no personal set: take back the one an
 *  earlier answer installed. A set the entitlement feed delivered stays. */
export function forgetIdentityPersonalSet(twitchUserId: string): void {
  const setId = identityInstalled.get(twitchUserId);
  if (!setId) return;
  identityInstalled.delete(twitchUserId);
  userSets.get(twitchUserId)?.delete(setId);
}

/** The sender's personal 7TV emotes, or undefined when they have none. */
export function personalEmotesFor(twitchUserId: string | undefined): EmoteLayer | undefined {
  if (!twitchUserId) return undefined;
  const sets = userSets.get(twitchUserId);
  if (!sets || sets.size === 0) return undefined;
  const layers = [...sets].map((id) => personalSets.get(id)).filter((l): l is EmoteLayer => !!l && l.size > 0);
  if (layers.length === 0) return undefined;
  if (layers.length === 1) return layers[0];
  const merged: EmoteLayer = new Map();
  for (const l of layers) for (const [k, v] of l) merged.set(k, v);
  return merged;
}

function setFrame(setId: string, op: number): string {
  return JSON.stringify({ op, d: { type: 'emote_set.update', condition: { object_id: setId } } });
}

// The owner's account: switching the channel's active set is a `user.update`
// on the owner, not an edit of either set.
function userFrame(userId: string, op: number): string {
  return JSON.stringify({ op, d: { type: 'user.update', condition: { object_id: userId } } });
}

function entitlementFrames(roomId: string, op: number): string[] {
  return ENTITLEMENT_TYPES.map((type) =>
    JSON.stringify({ op, d: { type, condition: { ctx: 'channel', platform: 'TWITCH', id: roomId } } }),
  );
}

/** Whether another room's subscription already covers this set or user, so
 *  one room's change never drops (or doubles) a subscription another needs. */
function inUseElsewhere(roomId: string, kind: 'set' | 'user', id: string | null): boolean {
  if (!id) return false;
  for (const [room, sub] of subscribed) {
    if (room === roomId) continue;
    if (kind === 'set' ? sub.sets.has(id) : sub.userId === id) return true;
  }
  return false;
}

/** The sets a room should be subscribed to: the one shown, and the one being
 *  loaded (subscribed before its load, so no edit to it is missed). */
function wantedSets(w: ChannelWatch): Set<string> {
  const sets = new Set<string>();
  if (w.map.seventvSetId) sets.add(w.map.seventvSetId);
  if (w.pending) sets.add(w.pending.setId);
  return sets;
}

function subscribeRoom(w: ChannelWatch) {
  if (subscribed.has(w.roomId)) return;
  const sub: RoomSubscription = { sets: wantedSets(w), userId: w.map.seventvUserId, entitlements: w.platform === 'TWITCH' };
  for (const set of sub.sets) if (!inUseElsewhere(w.roomId, 'set', set)) ws?.send(setFrame(set, OP_SUBSCRIBE));
  if (sub.userId && !inUseElsewhere(w.roomId, 'user', sub.userId)) ws?.send(userFrame(sub.userId, OP_SUBSCRIBE));
  if (sub.entitlements) for (const f of entitlementFrames(w.platformId, OP_SUBSCRIBE)) ws?.send(f);
  subscribed.set(w.roomId, sub);
}

function unsubscribeRoom(roomId: string) {
  const sub = subscribed.get(roomId);
  if (!sub) return;
  subscribed.delete(roomId);
  for (const set of sub.sets) if (!inUseElsewhere(roomId, 'set', set)) ws?.send(setFrame(set, OP_UNSUBSCRIBE));
  if (sub.userId && !inUseElsewhere(roomId, 'user', sub.userId)) ws?.send(userFrame(sub.userId, OP_UNSUBSCRIBE));
  if (sub.entitlements) for (const f of entitlementFrames(roomId, OP_UNSUBSCRIBE)) ws?.send(f);
}

/**
 * Move a room's set and owner subscriptions onto what it needs now: subscribe
 * the sets and owner it lacks, unsubscribe the ones it no longer needs. Waits
 * for the socket when it is down; the next join or resume brings it in line.
 */
function syncRoom(w: ChannelWatch) {
  const sub = subscribed.get(w.roomId);
  if (!sub || !ws || ws.readyState !== WebSocket.OPEN || awaitingResume) return;
  const want = wantedSets(w);
  for (const set of want) {
    if (sub.sets.has(set)) continue;
    sub.sets.add(set);
    if (!inUseElsewhere(w.roomId, 'set', set)) ws.send(setFrame(set, OP_SUBSCRIBE));
  }
  for (const set of [...sub.sets]) {
    if (want.has(set)) continue;
    sub.sets.delete(set);
    if (!inUseElsewhere(w.roomId, 'set', set)) ws.send(setFrame(set, OP_UNSUBSCRIBE));
  }
  const prevUser = sub.userId;
  const nextUser = w.map.seventvUserId;
  if (prevUser !== nextUser) {
    sub.userId = nextUser;
    if (prevUser && !inUseElsewhere(w.roomId, 'user', prevUser)) ws.send(userFrame(prevUser, OP_UNSUBSCRIBE));
    if (nextUser && !inUseElsewhere(w.roomId, 'user', nextUser)) ws.send(userFrame(nextUser, OP_SUBSCRIBE));
  }
}

/** Bring the session's subscriptions in line with the channels on the page. */
function reconcile() {
  for (const roomId of [...subscribed.keys()]) if (!channels.has(roomId)) unsubscribeRoom(roomId);
  for (const w of channels.values()) {
    if (subscribed.has(w.roomId)) syncRoom(w);
    else subscribeRoom(w);
  }
}

function scheduleRetry(w: ChannelWatch, attempt: number, run: () => void) {
  if (w.retry) clearTimeout(w.retry);
  w.retry = setTimeout(() => {
    w.retry = null;
    if (channels.get(w.roomId) === w) run();
  }, retryDelayMs(attempt));
}

/** Start loading `setId`: subscribe to it now and keep its edits until the
 *  load lands. A load of another set that was still running is abandoned. */
function beginLoad(w: ChannelWatch, setId: string) {
  if (w.pending?.setId === setId) return;
  if (w.pending && w.pending.setId !== w.map.seventvSetId) retiredSets.add(w.pending.setId);
  w.pending = { setId, buffer: [] };
  retiredSets.delete(setId);
  syncRoom(w);
}

/** Lay the edits kept during a load over the loaded set, in the order they
 *  arrived. Applying one the answer already carries changes nothing: a push
 *  sets the same id under the same name, a pull only removes that exact id. */
function withBufferedEdits(w: ChannelWatch, setId: string | null, layer: EmoteLayer): EmoteLayer {
  if (setId && w.pending?.setId === setId) for (const edit of w.pending.buffer) applySeventvChange(layer, edit);
  return layer;
}

/** Put a set's emotes into the channel's 7TV layer, in place: the map object
 *  the chat parser holds stays the same, so new messages use the new set at
 *  once and messages already on screen keep the art they were drawn with. */
function replaceChannelLayer(w: ChannelWatch, layer: EmoteLayer, setId: string | null, userId: string | null) {
  // Replacing a stand-in (7TV was down) heals the rows drawn with it; a switch
  // of the set only adds what the rows lack, so drawn art stays.
  const mode = w.map.seventvStale ? 'heal' : 'gain';
  const old = w.map.seventvSetId;
  if (old && old !== setId) retiredSets.add(old);
  if (setId) retiredSets.delete(setId);
  w.map.seventv.clear();
  for (const [k, v] of layer) w.map.seventv.set(k, v);
  w.map.seventvSetId = setId;
  w.map.seventvUserId = userId;
  w.map.seventvStale = false;
  w.pending = null;
  syncRoom(w);
  saveSeventvChannel(w.roomId, w.map);
  emitEmotesChanged({ kind: 'table', scope: w.roomId, mode });
}

/**
 * Read the channel from 7TV again and take whatever it says now: its owner,
 * its active set and that set's emotes. Used when 7TV did not answer at load,
 * after a resume that failed (edits were missed), when a switch dispatch did
 * not name the new set, and when the owner's linked account changed. Retries
 * with backoff while 7TV does not answer; the list on screen stays meanwhile.
 * Edits to the set shown are kept during the read and laid over its answer;
 * an answer naming a set not yet followed loads that set the same way.
 */
async function resyncChannel(w: ChannelWatch, attempt = 0): Promise<void> {
  const gen = ++w.gen;
  if (w.map.seventvSetId) beginLoad(w, w.map.seventvSetId);
  const answer = await fetchProvider(seventvUserUrl(w.platform, w.platformId));
  if (gen !== w.gen || channels.get(w.roomId) !== w) return;
  if (!answer.ok) {
    scheduleRetry(w, attempt, () => void resyncChannel(w, attempt + 1));
    return;
  }
  const fresh = seventvChannelOf(answer.body);
  if (fresh.userId !== w.map.seventvUserId) {
    w.map.seventvUserId = fresh.userId;
    syncRoom(w);
  }
  if (fresh.setId && fresh.setId !== w.pending?.setId) {
    // Not followed yet, so edits made since 7TV answered could be missing:
    // subscribe first, then load it whole.
    void adoptSet(w, fresh.setId, 0, true);
    return;
  }
  replaceChannelLayer(w, withBufferedEdits(w, fresh.setId, fresh.layer), fresh.setId, fresh.userId);
}

/**
 * The owner switched the channel to `setId`. A switch to the set already shown
 * is a replay or another connection's, and is ignored. The new set is
 * subscribed first, then loaded whole, and the edits that arrived meanwhile
 * are laid over it; until it answers, the old list stays and the load is
 * retried with backoff.
 */
async function adoptSet(w: ChannelWatch, setId: string, attempt = 0, force = false): Promise<void> {
  if (!force && attempt === 0 && setId === w.map.seventvSetId && !w.map.seventvStale) return;
  const gen = ++w.gen;
  beginLoad(w, setId);
  const answer = await fetchProvider(`https://7tv.io/v3/emote-sets/${encodeURIComponent(setId)}`);
  if (gen !== w.gen || channels.get(w.roomId) !== w) return;
  if (!answer.ok || !answer.body) {
    scheduleRetry(w, attempt, () => void adoptSet(w, setId, attempt + 1, true));
    return;
  }
  const layer: EmoteLayer = new Map();
  addSeventv(layer, answer.body.emotes);
  replaceChannelLayer(w, withBufferedEdits(w, setId, layer), setId, w.map.seventvUserId);
}

/** The set a `user.update` switched a connection to, read from the nested
 *  change map: `updated[] { key: "connections", value: [ { key: "emote_set",
 *  value: set } ] }` (the app and the 7TV extension read it the same way).
 *  `id` undefined: the set changed (or was taken off) but the dispatch did not
 *  name a set, so the channel is read again. Null when the update is not a set
 *  switch. */
export function seventvSetSwitch(body: any): { id: string | undefined } | null {
  const updated = Array.isArray(body?.updated) ? body.updated : [];
  let found: { id: string | undefined } | null = null;
  for (const u of updated) {
    if (u?.key !== 'connections' || !Array.isArray(u.value)) continue;
    for (const f of u.value) {
      if (f?.key === 'emote_set') {
        const id = f.value?.id;
        return { id: typeof id === 'string' ? id : undefined };
      }
      if (f?.key === 'emote_set_id') found = { id: typeof f.value === 'string' ? f.value : undefined };
    }
  }
  return found;
}

/** Whether a `user.update` took this channel's account off the 7TV user (the
 *  channel was linked to a different 7TV account). */
function unlinkedChannel(body: any, w: ChannelWatch): boolean {
  const pulled = Array.isArray(body?.pulled) ? body.pulled : [];
  return pulled.some(
    (p: any) => p?.key === 'connections' && p.old_value?.platform === w.platform && String(p.old_value?.id) === w.platformId,
  );
}

function handleUserUpdate(body: any) {
  const userId = body?.id;
  if (typeof userId !== 'string') return;
  const owned = [...channels.values()].filter((w) => w.map.seventvUserId === userId);
  if (owned.length === 0) return;
  const change = seventvSetSwitch(body);
  for (const w of owned) {
    if (unlinkedChannel(body, w)) void resyncChannel(w);
    else if (!change) continue;
    else if (change.id) void adoptSet(w, change.id);
    else void resyncChannel(w);
  }
}

function ensureConnected() {
  if (ws || retryTimer || channels.size === 0) return;
  connect();
}

function connect() {
  retryTimer = null;
  if (channels.size === 0) return;
  let socket: WebSocket;
  try {
    socket = new WebSocket(EVENTAPI_URL);
  } catch {
    scheduleReconnect();
    return;
  }
  ws = socket;
  socket.onmessage = (ev) => {
    if (ws !== socket) return;
    armDeadTimer();
    let msg: { op?: number; d?: any };
    try {
      msg = JSON.parse(typeof ev.data === 'string' ? ev.data : '');
    } catch {
      return;
    }
    handleFrame(msg);
  };
  socket.onclose = (ev) => {
    if (ws !== socket) return;
    ws = null;
    clearTimers();
    // A session we were in can be resumed; one that never said HELLO cannot.
    resumeFrom = sessionId ?? resumeFrom;
    sessionId = null;
    if (ev.code === 4007) delayFloor = MAINTENANCE_DELAY_MS;
    else if (ev.code === 4005) delayFloor = RATE_LIMITED_DELAY_MS;
    scheduleReconnect();
  };
  socket.onerror = () => socket.close();
}

function handleFrame(msg: { op?: number; d?: any }) {
  switch (msg.op) {
    case OP_HELLO: {
      sessionId = typeof msg.d?.session_id === 'string' ? msg.d.session_id : null;
      const hb = Number(msg.d?.heartbeat_interval);
      heartbeatMs = hb > 0 ? hb : DEFAULT_HEARTBEAT_MS;
      backoff = BACKOFF_MIN_MS;
      armDeadTimer();
      if (resumeFrom) {
        // The server restores the old subscriptions and replays what we missed.
        // Anything but an ACK for it in the window means starting fresh.
        awaitingResume = true;
        ws?.send(JSON.stringify({ op: OP_RESUME, d: { session_id: resumeFrom } }));
        resumeTimer = setTimeout(() => startFresh(true), RESUME_WAIT_MS);
      } else {
        startFresh(false);
      }
      return;
    }
    case OP_ACK:
      if (awaitingResume && msg.d?.command === 'RESUME') {
        if (msg.d?.data?.success === false) {
          startFresh(true);
          return;
        }
        awaitingResume = false;
        resumeFrom = null;
        if (resumeTimer) clearTimeout(resumeTimer);
        resumeTimer = null;
        // The old subscriptions are back; add channels that arrived while the
        // socket was down and drop the ones that left.
        reconcile();
      }
      return;
    case OP_DISPATCH:
      handleDispatch(msg.d);
      return;
    case OP_RECONNECT:
    case OP_END_OF_STREAM:
      ws?.close();
      return;
    default:
      return;
  }
}

function startFresh(afterFailedResume: boolean) {
  if (resumeTimer) clearTimeout(resumeTimer);
  resumeTimer = null;
  awaitingResume = false;
  resumeFrom = null;
  if (!ws || ws.readyState !== WebSocket.OPEN) return;
  // A new session starts with nothing subscribed.
  subscribed.clear();
  reconcile();
  // Changes made while we were away were not replayed; take the channel sets again.
  if (afterFailedResume) for (const w of channels.values()) void resyncChannel(w);
}

function handleDispatch(d: any) {
  const type: string = d?.type ?? '';
  const body = d?.body;
  if (!body) return;
  if (type === 'user.update') {
    handleUserUpdate(body);
    return;
  }
  if (type === 'emote_set.update') {
    const setId = body.id;
    if (typeof setId !== 'string') return;
    let channelSet = false;
    let changes: SeventvEmoteChange[] | null = null;
    for (const w of channels.values()) {
      const loading = w.pending?.setId === setId;
      if (loading) {
        // The set is loading: keep the edit, in order, for the load to lay
        // over its answer.
        const buffer = w.pending!.buffer;
        buffer.push(body);
        if (buffer.length > MAX_BUFFERED_EDITS) buffer.shift();
      }
      const shown = w.map.seventvSetId === setId;
      if (shown) {
        applySeventvChange(w.map.seventv, body);
        // Written through, so a restart while 7TV is down shows this edit.
        saveSeventvChannel(w.roomId, w.map);
        emitEmotesChanged({ kind: 'table', scope: w.roomId, mode: 'gain' });
      }
      if (shown || loading) {
        channelSet = true;
        if (w.listeners.size > 0) {
          changes ??= seventvChangesFromDispatch(body);
          if (changes.length > 0) for (const listener of w.listeners) listener(changes);
        }
      }
    }
    if (channelSet || retiredSets.has(setId)) return;
    // Anything else on this socket is a chatter's personal set: the entitlement
    // subscription delivers them, as a snapshot on arrival and as edits after.
    const layer = personalSets.get(setId) ?? new Map();
    applySeventvChange(layer, body, true);
    rememberSet(setId, layer);
    return;
  }
  if (type.startsWith('entitlement.')) {
    const obj = body.object;
    const conns: any[] = Array.isArray(obj?.user?.connections) ? obj.user.connections : [];
    const twitchId: unknown = conns.find((c) => c?.platform === 'TWITCH')?.id;
    if (obj?.kind === 'PAINT' || obj?.kind === 'BADGE') {
      // A chatter put on or took off a paint or badge (or arrived wearing
      // one): whoever draws them asks for their cosmetics again.
      if (typeof twitchId === 'string' && twitchId) for (const listener of cosmeticListeners) listener(twitchId);
      return;
    }
    if (obj?.kind !== 'EMOTE_SET') return;
    const setId: unknown = obj.ref_id;
    if (typeof setId !== 'string' || typeof twitchId !== 'string') return;
    if (type === 'entitlement.delete') {
      userSets.get(twitchId)?.delete(setId);
      return;
    }
    const sets = userSets.get(twitchId) ?? new Set<string>();
    sets.add(setId);
    userSets.set(twitchId, sets);
    if (!personalSets.has(setId)) setTimeout(() => queueFetch(setId), SNAPSHOT_GRACE_MS);
  }
}

function rememberSet(setId: string, layer: EmoteLayer, announce = true) {
  personalSets.delete(setId);
  personalSets.set(setId, layer);
  if (personalSets.size > MAX_PERSONAL_SETS) {
    const oldest = personalSets.keys().next().value;
    if (oldest !== undefined) personalSets.delete(oldest);
  }
  if (!announce) return;
  // Rows their owners sent before the set arrived can draw it now.
  const owners: string[] = [];
  for (const [user, sets] of userSets) if (sets.has(setId)) owners.push(user);
  if (owners.length > 0) emitEmotesChanged({ kind: 'personal', userIds: owners });
}

function queueFetch(setId: string) {
  if (personalSets.has(setId) || pendingFetch.has(setId)) return;
  pendingFetch.add(setId);
  fetchQueue.push(setId);
  pumpFetches();
}

function pumpFetches() {
  while (activeFetches < FETCH_CONCURRENCY && fetchQueue.length > 0) {
    const setId = fetchQueue.shift()!;
    activeFetches += 1;
    void fetchPersonalSet(setId).finally(() => {
      activeFetches -= 1;
      pendingFetch.delete(setId);
      pumpFetches();
    });
  }
}

async function fetchPersonalSet(setId: string) {
  if (personalSets.has(setId)) return;
  // A set that did not answer is asked again the next time its entitlement is
  // delivered; it is never kept as empty.
  const answer = await fetchProvider(`https://7tv.io/v3/emote-sets/${encodeURIComponent(setId)}`);
  if (!answer.ok || !answer.body) return;
  const layer: EmoteLayer = new Map();
  for (const e of Array.isArray(answer.body.emotes) ? answer.body.emotes : []) {
    if (!isPersonalUseEmote(e)) continue;
    const row = seventvActiveEmote(e);
    if (row) layer.set(row.name, { ...row.info, personal: true });
  }
  // A snapshot may have landed while this was in flight; it is as fresh.
  if (!personalSets.has(setId)) rememberSet(setId, layer);
}

function armDeadTimer() {
  if (deadTimer) clearTimeout(deadTimer);
  deadTimer = setTimeout(() => ws?.close(), heartbeatMs * HEARTBEAT_MISSES);
}

function clearTimers() {
  if (deadTimer) clearTimeout(deadTimer);
  if (resumeTimer) clearTimeout(resumeTimer);
  deadTimer = null;
  resumeTimer = null;
  awaitingResume = false;
}

function scheduleReconnect() {
  if (channels.size === 0 || retryTimer) return;
  const jitter = Math.random() * 0.3 * backoff;
  const delay = Math.max(delayFloor, backoff + jitter);
  delayFloor = 0;
  backoff = Math.min(backoff * 2, BACKOFF_MAX_MS);
  retryTimer = setTimeout(connect, delay);
}

function shutdown() {
  if (retryTimer) clearTimeout(retryTimer);
  retryTimer = null;
  clearTimers();
  const socket = ws;
  ws = null;
  sessionId = null;
  resumeFrom = null;
  subscribed.clear();
  socket?.close();
}
